import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  deleteDocument,
  duplicateDocument,
  updateDocument,
  type DocDocument,
} from '@/lib/documents';
import DocumentEditorModal from '@/pages/dashboard/apps/file-manager/components/DocumentEditorModal';

/**
 * Édition d'un document depuis le compte utilisateur (/mon-compte/documents/:id).
 */
export default function ClientDocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [document, setDocument] = useState<DocDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const numeric = Number(id);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setError('Document introuvable.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('documents')
        .select('*')
        .eq('id', numeric)
        .maybeSingle();
      if (dbError) throw dbError;
      if (!data) throw new Error('Document introuvable ou supprimé.');
      setDocument(data as DocDocument);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger ce document.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (content: Record<string, unknown>, name: string) => {
    if (!document) return;
    setSaving(true);
    try {
      await updateDocument(document.id, content, name, user ? { id: user.id, name: user.name } : undefined);
      setDocument((prev) => (prev ? { ...prev, name, content, updated_at: new Date().toISOString() } : prev));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!document) return;
    await deleteDocument(document.id);
    navigate('/mon-compte/documents', { replace: true });
  };

  const handleDuplicate = async (content: Record<string, unknown>, name: string) => {
    if (!document) return;
    const copy = await duplicateDocument({ ...document, content, name });
    navigate(`/mon-compte/documents/${copy.id}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 min-h-screen bg-background-50">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 min-h-screen bg-background-50">
        <i className="ri-error-warning-line text-3xl text-foreground-300"></i>
        <p className="text-sm text-foreground-600">{error || 'Document introuvable.'}</p>
        <button
          onClick={() => navigate('/mon-compte/documents')}
          className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium cursor-pointer hover:bg-background-200/70"
        >
          Retour à mes documents
        </button>
      </div>
    );
  }

  // Plein écran au-dessus du menu latéral de l'espace client (z-50) : l'éditeur
  // ne doit jamais être recouvert par la navigation.
  return (
    <div className="fixed inset-0 z-[70] bg-white">
      <DocumentEditorModal
        document={document}
        saving={saving}
        currentUser={{ id: user?.id ?? 0, name: user?.name ?? '' }}
        onSave={handleSave}
        onClose={() => navigate('/mon-compte/documents')}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        showFormButton={false}
      />
    </div>
  );
}